import * as React from 'react'
import { cva } from 'class-variance-authority'
import { ArrowRight, Loader2, XIcon } from 'lucide-react'
import { Slot as SlotPrimitive } from 'radix-ui'

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/utilities/ui'

import type { VariantProps } from 'class-variance-authority'

/*
Button API:

variant: 'primary' | 'outline' | 'accent' | 'link' | 'icon' | 'iconOutline' | 'iconAccent | 'filter' | 'destructive' (default: 'primary')
color: 'default' | 'inverse' (default: 'default') - not valid for accent or filter
size: 'default' | 'small' (default: 'default') - not valid for filter
ghost: boolean (default: false) - for primary/accent/icon, removes background

iconOutline & filter: standalone, no color/ghost variants

Other props:
- className?: string
- asChild?: boolean (default: false)
- icon?: boolean (default: true) - show trailing icon
- customIcon?: React.ReactNode
- labelClassName?: string
*/

const buttonVariants = cva(
  'group inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl font-family-body text-sm font-semibold whitespace-nowrap transition-all outline-none focus-visible:ring-3 focus-visible:ring-evergreen-500 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: '',
        outline: 'border',
        accent: '',
        link: 'flex items-center p-0 transition-all duration-200',
        icon: '',
        iconOutline: '',
        iconAccent: '',
        filter: 'group w-full justify-between text-reg',
        destructive: '',
      },
      color: {
        default: '',
        inverse: '',
      },
      size: {
        default: 'h-10 px-4 py-1.5',
        small: 'h-8 px-2.5 py-1 text-xs',
      },
      ghost: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      // Primary + default (solid)
      {
        variant: 'primary',
        color: 'default',
        ghost: false,
        className: 'bg-primary text-primary-foreground hover:bg-primary/90',
      },
      // Primary + inverse (solid)
      {
        variant: 'primary',
        color: 'inverse',
        ghost: false,
        className: 'bg-primary-inverse text-primary-inverse-foreground hover:bg-primary-inverse/80',
      },
      // Primary + default + ghost
      {
        variant: 'primary',
        color: 'default',
        ghost: true,
        className:
          'bg-transparent text-primary-inverse-foreground hover:text-primary-inverse-foreground/90 hover:bg-neutral-900/15',
      },
      // Primary + inverse + ghost
      {
        variant: 'primary',
        color: 'inverse',
        ghost: true,
        className:
          'bg-transparent text-primary-foreground hover:text-primary-foreground/90 hover:bg-primary-inverse/15',
      },
      // Outline + default
      {
        variant: 'outline',
        color: 'default',
        className:
          'bg-outline-background text-outline-foreground border-border hover:bg-neutral-100 hover:border-border-hover',
      },
      // Outline + inverse
      {
        variant: 'outline',
        color: 'inverse',
        className:
          'bg-transparent text-secondary border-border-inverse hover:bg-secondary/80 hover:text-secondary-foreground hover:border-border-inverse/80',
      },
      // Accent
      {
        variant: 'accent',
        color: 'default',
        ghost: false,
        className: 'bg-evergreen-800 text-primary-foreground hover:bg-evergreen-700',
      },
      {
        variant: 'accent',
        color: 'inverse',
        ghost: false,
        className: 'bg-primary-foreground text-evergreen-800 hover:bg-evergreen-100',
      },
      // Accent + ghost
      {
        variant: 'accent',
        ghost: true,
        className:
          'bg-transparent text-evergreen-800 hover:text-evergreen-700 hover:bg-evergreen-900/15',
      },
      // Accent + inverse
      {
        variant: 'accent',
        color: 'inverse',
        className: 'text-secondary-foreground bg-neutral-100 hover:bg-neutral-200',
      },
      // Link + default
      {
        variant: 'link',
        color: 'default',
        className:
          'bg-transparent text-outline-foreground hover:opacity-70 hover:bg-none px-0 py-0 justify-start items-center transition-all duration-200',
      },
      // Link + inverse
      {
        variant: 'link',
        color: 'inverse',
        className:
          'bg-transparent text-primary-foreground hover:opacity-70 hover:bg-none px-0 py-0 justify-start items-center transition-all duration-200',
      },
      // Icon + default (solid) - same as primary
      {
        variant: 'icon',
        color: 'default',
        ghost: false,
        className: 'bg-primary text-primary-foreground hover:bg-primary/90 h-10 w-10 p-1 shrink-0',
      },
      // Icon + inverse (solid)
      {
        variant: 'icon',
        color: 'inverse',
        ghost: false,
        className:
          'bg-primary-inverse text-primary-inverse-foreground hover:bg-primary-inverse/80 h-10 w-10 p-1 shrink-0',
      },
      // Icon + ghost
      {
        variant: 'icon',
        ghost: true,
        className: 'bg-transparent text-neutral-700 hover:bg-neutral-900/10 h-10 w-10 shrink-0',
      },
      // IconOutline (standalone, no color/ghost variants)
      {
        variant: 'iconOutline',
        className:
          'size-10 bg-card text-foreground border border-border hover:bg-transparent p-1 shrink-0',
      },
      // IconOutline + small size
      {
        variant: 'iconOutline',
        size: 'small',
        className:
          'size-8 bg-card text-foreground border border-border hover:bg-transparent p-1 shrink-0',
      },
      // IconAccent (standalone, no color/ghost variants)
      {
        variant: 'iconAccent',
        className:
          'size-10 bg-evergreen-800 text-primary-foreground hover:bg-evergreen-700 p-1 shrink-0',
      },
      // IconAccent + small size
      {
        variant: 'iconAccent',
        size: 'small',
        className:
          'size-8 bg-evergreen-800 text-primary-foreground hover:bg-evergreen-700 p-1 shrink-0',
      },
      // Filter (standalone, no color/ghost variants)
      {
        variant: 'filter',
        className:
          'bg-transparent text-muted-foreground px-3 [&.selected]:text-secondary-foreground [&.selected]:bg-secondary hover:bg-secondary/80 border-none hover:border-none',
      },
      // Link size override (no height)
      {
        variant: 'link',
        size: 'default',
        className: 'h-auto',
      },
      {
        variant: 'link',
        size: 'small',
        className: 'h-auto',
      },
      {
        variant: 'destructive',
        ghost: false,
        className:
          'bg-destructive/10 text-destructive hover:bg-destructive/20 focus-within:border-destructive/40 focus-within:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-within:ring-destructive/40',
      },
      // Accent + ghost
      {
        variant: 'destructive',
        ghost: true,
        className:
          'bg-transparent text-destructive hover:text-destructive/90 hover:bg-destructive/15 focus-within:border-destructive/40 focus-within:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-within:ring-destructive/40',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      color: 'default',
      size: 'default',
      ghost: false,
    },
  },
)

// Icon wrapper colors based on variant + color
const getIconWrapperClass = (
  variant: ButtonProps['variant'],
  color: ButtonProps['color'],
  size: 'large' | 'default',
  ghost: boolean,
): string => {
  let sizeString = ''

  if (size === 'default') {
    sizeString = 'p-0.5 size-4'
  } else if (size === 'large') {
    sizeString = 'p-0.5 size-6'
  }

  // For icon variant, no wrapper needed
  if (variant === 'icon' || variant === 'iconOutline' || variant === 'iconAccent') return ''

  // Filter has its own icon styling
  if (variant === 'filter') {
    return 'bg-transparent text-muted-foreground group-[.selected]:text-secondary-foreground'
  }

  // Link and ghost share similar icon wrapper colors
  if (variant === 'link' || ghost) {
    return color === 'inverse'
      ? `${sizeString} rounded-full inline-flex items-center justify-center bg-border-inverse text-outline-inverse-foreground`
      : `${sizeString} rounded-full inline-flex items-center justify-center bg-border text-outline-foreground`
  }

  // Outline
  if (variant === 'outline') {
    return color === 'inverse'
      ? `${sizeString} rounded-full inline-flex items-center justify-center bg-border-inverse text-primary-foreground group-hover:text-secondary-foreground`
      : `${sizeString} rounded-full inline-flex items-center justify-center bg-border text-neutral-700`
  }

  // Accent
  if (variant === 'accent') {
    return `${sizeString} rounded-full inline-flex items-center justify-center bg-enoki/12 text-white`
  }

  // Destructive
  if (variant === 'destructive') {
    return `${sizeString} rounded-full inline-flex items-center justify-center bg-destructive/90 text-white`
    // return 'bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40'
  }

  // Primary (default)
  return color === 'inverse'
    ? `${sizeString} rounded-full inline-flex items-center justify-center bg-neutral-200 text-neutral-700 hover:text-neutral-750`
    : `${sizeString} rounded-full inline-flex items-center justify-center bg-neutral-600 text-primary-foreground`
}

export interface ButtonProps
  extends
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  icon?: boolean
  iconAlign?: 'left' | 'right'
  iconWrapper?: boolean
  iconSize?: 'default' | 'large'
  customIcon?: React.ReactNode
  ghost?: boolean
  color?: 'default' | 'inverse'
  labelClassName?: string
  tooltipText?: string
  dialogTrigger?: boolean
  loading?: boolean
}

const Icon = ({
  variant,
  color,
  ghost,
  iconSize = 'default',
  customIcon,
  iconWrapper,
  loading,
}: {
  variant: ButtonProps['variant']
  color: ButtonProps['color']
  ghost: boolean
  iconSize?: 'default' | 'large'
  customIcon?: React.ReactNode
  iconWrapper: boolean
  loading: boolean
}) => {
  if (loading) {
    return <Loader2 className="size-4 animate-spin" />
  }

  // For icon variant, render directly without wrapper
  if (variant === 'icon' || !iconWrapper) {
    return customIcon || null
  }

  const wrapperClass = getIconWrapperClass(variant, color, iconSize, ghost)

  // Determine the icon content
  const iconElement =
    customIcon ??
    (() => {
      if (variant === 'filter') return null
      if (variant === 'destructive') return <XIcon className="size-3" />
      return <ArrowRight className="size-3" />
    })()

  return <span className={wrapperClass}>{iconElement}</span>
}

const TooltipWrapper = ({
  children,
  tooltipText,
}: {
  children: React.ReactNode
  tooltipText: string
}) => {
  const [open, setOpen] = React.useState(false)

  const handleOpenChange = (open: boolean) => {
    setOpen(open)
  }

  return (
    <Tooltip delayDuration={0} open={open} onOpenChange={handleOpenChange}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent className="px-2 py-1">
        <p>{tooltipText}</p>
      </TooltipContent>
    </Tooltip>
  )
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      asChild = false,
      icon = true,
      iconAlign = 'right',
      iconWrapper = true,
      iconSize = 'default',
      ghost = false,
      className,
      size,
      variant,
      color,
      children,
      customIcon,
      labelClassName,
      tooltipText,
      loading = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? SlotPrimitive.Slot : 'button'

    let buttonElement

    // Filter bypasses icon and asChild
    if (variant === 'filter') {
      buttonElement = (
        <Comp
          className={cn(buttonVariants({ variant, size, color, ghost }), className)}
          ref={ref}
          disabled={loading}
          {...props}
        >
          {children}
        </Comp>
      )
    }

    // When using asChild, pass children directly to SlotPrimitive.Slot without wrapping
    if (asChild) {
      // Handle case where children exists
      if (React.isValidElement(children)) {
        const childWithIcon = React.cloneElement(
          children as React.ReactElement,
          {},
          <>
            {(children as React.ReactElement<{ children: React.ReactNode }>).props.children}
            {icon && (
              <Icon
                variant={variant}
                color={color}
                ghost={ghost}
                customIcon={customIcon}
                iconSize={iconSize}
                iconWrapper={iconWrapper}
                loading={loading}
              />
            )}
          </>,
        )

        buttonElement = (
          <Comp
            className={cn(buttonVariants({ variant, size, color, ghost }), className)}
            ref={ref}
            disabled={loading}
            {...props}
          >
            {childWithIcon}
          </Comp>
        )
      }

      // Handle case where no children but customIcon exists
      if (variant === 'icon' && customIcon) {
        buttonElement = (
          <Comp
            className={cn(buttonVariants({ variant, size, color, ghost }), className)}
            ref={ref}
            {...props}
          >
            <Icon
              variant={variant}
              color={color}
              ghost={ghost}
              customIcon={customIcon}
              iconSize={iconSize}
              iconWrapper={iconWrapper}
              loading={loading}
            />
          </Comp>
        )
      }
    } else {
      // When not using asChild, wrap in span
      buttonElement = (
        <Comp
          className={cn(buttonVariants({ variant, size, color, ghost }), className)}
          ref={ref}
          disabled={loading}
          {...props}
        >
          <span
            className={cn('flex w-fit items-center justify-center gap-2 leading-6', labelClassName)}
          >
            {icon && iconAlign === 'left' && (
              <Icon
                variant={variant}
                color={color}
                ghost={ghost}
                customIcon={customIcon}
                iconSize={iconSize}
                iconWrapper={iconWrapper}
                loading={loading}
              />
            )}
            {children}
            {icon && iconAlign === 'right' && (
              <Icon
                variant={variant}
                color={color}
                ghost={ghost}
                customIcon={customIcon}
                iconSize={iconSize}
                iconWrapper={iconWrapper}
                loading={loading}
              />
            )}
          </span>
        </Comp>
      )
    }

    if (tooltipText) {
      buttonElement = <TooltipWrapper tooltipText={tooltipText}>{buttonElement}</TooltipWrapper>
    }

    return buttonElement
  },
)

Button.displayName = 'Button'

export { Button, buttonVariants }
